<template>
  <div class="landing-page-v2 future-proof-2024">
    <div class="hero">
      <div class="container">
        <div class="row">
          <div class="col col-7">
            <h1>
              <div class="code-block">
                <img :src="require('@itbpbg/lms-components/src/assets/images/campaign-future-proof/learn-future-proof.webp')" alt="learn future-proof">
              </div>
              <span class="highlight blue special">
                DATA AND AI SKILLS
              </span>
              <span class="block text-2">
                <span class="small">at</span> <span class="highlight yellow">{{ promotion.discount }}% OFF</span>
              </span>
            </h1>
            <div class="desktop-only">
              <div class="subtitle">
                Become a certified
                <div class="animation" :class="{ animate: heroAnimation.animate }">
                  {{ heroAnimation.strings[heroAnimation.index] }}
                </div>
                and double your salary within a year with our practice-based courses taught by instructors from top-tier companies.
              </div>
              <Companies
                v-if="companies && companies.length"
                :companies="companies" title="" no-fill
              />
            </div>
          </div>
          <div class="holder col col-5">
            <div class="card">
              <p class="label">
                Limited-Time Offer
              </p>
              <p class="old-price">
                <span class="del">&nbsp;{{ page.prices.basePrice | formatCurrency(prices.currency, { minimumFractionDigits: 2 }) }}&nbsp;</span>
              </p>
              <p class="new-price">
                <span>{{ priceSplitted[0] | formatCurrency(prices.currency, { minimumFractionDigits: 0 }) }}</span>
                <span v-if="priceSplitted[1]" class="medium">.{{ priceSplitted[1] }}</span>
                <span class="small">/year</span>
              </p>
              <p class="text">
                Billed Annually
              </p>
              <CounterWithDiscount
                v-if="countdown"
                :key="promotion.discount"
                :end-date="countdownEndDate"
                counter-class="filled"
              />
              <nuxt-link :to="`/checkout/?plan=annual&coupon=${promotion.coupon}`" class="btn-primary campaign-1 submit">
                Save {{ promotion.discount }}%
              </nuxt-link>
              <template v-if="$config.platform === 'ds'">
                <p class="card-text">
                  30-day money-back guarantee
                </p>
                <p class="card-text">
                  Price-lock guarantee
                </p>
              </template>
              <template v-else>
                <p class="card-text">
                  Price-lock guarantee
                </p>
                <p class="card-text">
                  30-day money-back guarantee
                </p>
              </template>
              <div v-if="assets.hero.stamp" class="stamp">
                <img :src="require(`../assets/images/campaigns/${$config.platform}-fp2024-hero-stamp.webp`)">
              </div>
            </div>
          </div>
          <div class="mobile-only">
            <div class="subtitle">
              Become a certified
              <div class="animation" :class="{ animate: heroAnimation.animate }">
                {{ heroAnimation.strings[heroAnimation.index] }}
              </div>
              and double your salary within a year with our practice-based courses taught by instructors from top-tier companies.
            </div>
            <Companies
              v-if="companies && companies.length"
              :companies="companies" title="" no-fill
            />
          </div>
        </div>
      </div>
    </div>
    <div class="section courses-extended">
      <div class="head container">
        <h2>Prepare for the <span class="highlight yellow">Future of Work</span></h2>
        <h3 class="muted">
          What’s your ideal job?
        </h3>
        <ul>
          <li
            v-for="(g, idx) in cGroups" :key="idx" :class="{ active: idx === courseGroupIdx }"
            @click="switchCourseGroup(idx)"
          >
            {{ g.name }}
          </li>
        </ul>
        <p class="description">
          Become a certified
          <span class="highlight blue">{{ cGroup.description.title }}<span class="mobile-only">,</span></span><span class="desktop-only">,</span> earning
          <span class="static-salary-container">
            <span class="animation-salary" :class="{ animated: salaryAnimation.animatedOnScroll }">
              <span
                class="holder animation" :class="{ active: salaryAnimation.activate }"
                :style="`--width: ${salaryAnimation.width || 188}px`"
                v-html="salary"
              />
            </span>
          </span>
        </p>
      </div>
      <Courses :courses="cGroupCourses" :offset="-2" heading="h3" />
      <div class="btn-holder">
        <button
          class="btn-primary btn-lg"
          :data-event="`${$route.path.replace(/\//g, '')}_v1_courses.click`"
          @click="subscribe"
        >
          Start at {{ promotion.discount }}% OFF
        </button>
      </div>
    </div>
    <div class="section stats">
      <StatsIcons
        class="container"
        :stats="stats" colored auto-width
        :order="['students', 'courses', 'projects']"
      />
    </div>
    <div class="section second">
      <div class="instructors-slider">
        <SliderInfinite :offset="0" heading="h2" :title="instructorsTitle">
          <div
            v-for="instructor in instructors"
            :key="instructor.id"
            slot="slide"
          >
            <nuxt-link :to="`/instructors/${instructor.slug}`">
              <img :src="instructorImage(instructor)" :alt="instructor.name">
            </nuxt-link>
          </div>
        </SliderInfinite>
      </div>
      <div class="container instructors-footer">
        <Companies
          v-if="companiesSmall && companiesSmall.length"
          :companies="companiesSmall" title="Trusted by employees from:" no-fill
        />
        <div class="trustpilot-review">
          <IconTrustpilot />
          <Rating :reviews="{ rating: 4.9 }" />
        </div>
        <div class="btn-holder">
          <button
            class="btn-primary btn-lg"
            :data-event="`${$route.path.replace(/\//g, '')}_v1_instructors.click`"
            @click="subscribe"
          >
            Learn at {{ promotion.discount }}% OFF
          </button>
        </div>
      </div>
    </div>
    <div class="section master">
      <div class="container">
        <div class="row">
          <div class="col col-6">
            <h3>Master Essential Data and AI Skills</h3>
            <p class="muted">
              Become fluent in the leading tools employers look for, practice your skills with interactive exercises, and apply your knowledge to real-world projects.
            </p>
          </div>
          <div class="col col-6">
            <img :src="require(`../assets/images/campaigns/${$config.platform}-fp2024-master${$store.state.layout === 'mobile' ? '-mobile' : ''}.webp`)">
          </div>
        </div>
      </div>
    </div>
    <div class="section graduations">
      <Videos v-if="$config.platform === 'ds'" :videos="videos" :video-js-loaded="videoJsLoaded">
        <template slot="title">
          Join <img src="../assets/images/logo-extended.webp" alt="365 Data Science">
          <p>Trusted by Over <span class="highlight yellow">2 Million</span> Learners Worldwide</p>
        </template>
      </Videos>
    </div>
    <section class="limited-offer-holder">
      <LimitedOffer
        :options="limitedOfferOptions"
        :features="footerFeatures"
        campaign-page class-name="col-8"
        @subscribe="$router.push(`/checkout/?plan=annual&coupon=${promotion.coupon}`)"
      >
        <template slot="pre-title">
          <span>The offer is valid until July 31</span>
        </template>
      </LimitedOffer>
      <img
        v-show="$store.state.layout === 'desktop'"
        :src="require(`../assets/images/campaigns/${assets.footer.background}`)"
        class="limited-offer-background desktop-only"
      >
      <img
        :src="require(`../assets/images/campaigns/${assets.footer.backgroundMobile}`)"
        class="limited-offer-background mobile-only"
      >
    </section>
    <client-only>
      <BottomBanner
        v-if="$store.state.layout === 'desktop'"
        :key="1 + countdownEndDate.getTime()"
        :options="bottomBannerOptions"
        :discount="promotion.discount"
        :closed="bannerClosed"
        :style="{ 'background-image': `url(${require(`../assets/images/campaigns/${$config.platform}-fp2024-bg-bottom-banner.webp`)})` }"
        @subscribe="subscribe"
        @close="closeBanner"
      >
        <CounterWithDiscount
          slot="countdown"
          :end-date="countdownEndDate" banner
          discount-text="Current Discount"
          counter-class="filled"
        />
      </BottomBanner>
    </client-only>
  </div>
</template>

<script>
import { CAMPAIGN_PROMOTIONS } from '@itbpbg/lms-components/src/utils/campaigns';
import { getPromotion, dateInRightInterval } from '@itbpbg/lms-components/src/utils/helper';
import global from '@itbpbg/lms-components/src/mixins/global';
import Companies from '@itbpbg/lms-components/src/Campaigns/Companies';
import SliderInfinite from '@itbpbg/lms-components/src/components/SliderInfinite';
import Rating from '@itbpbg/lms-components/src/Courses/Rating';
import Videos from '@itbpbg/lms-components/src/Campaigns/Videos';
import CounterWithDiscount from '@itbpbg/lms-components/src/CounterWithDiscount';
import Courses from '../components/Campaigns/Courses';
import StatsIcons from '../components/Campaigns/StatsIcons';
import LimitedOffer from '../components/Landing/LimitedOffer';
import BottomBanner from '../components/Landing/BottomBanner';
import IconTrustpilot from '../assets/svg/icon-trustpilot.svg?vue-component';

export default {
  components: {
    Companies,
    SliderInfinite,
    Courses,
    StatsIcons,
    IconTrustpilot,
    Rating,
    Videos,
    CounterWithDiscount,
    LimitedOffer,
    BottomBanner,
  },
  mixins: [global],
  async asyncData({
    $axios,
    $config,
    route,
    error,
  }) {
    const {
      page, courses, stats, projects, coursesGroups,
    } = await $axios.$get('/pages/campaign/future-proof-2024?projects=1&prices=1&courses=1')
      .catch(() => error({ statusCode: 404, message: 'Page not found' }));

    if (!page) {
      return error({ statusCode: 404, message: 'Page not found' });
    }

    page.fullPath = `${$config.siteUrl}${route.fullPath}`;
    page.created = page.created || new Date();
    page.updated = page.updated || new Date();

    return {
      courses, stats, page, projects, coursesGroups,
    };
  },
  data() {
    return {
      assets: {
        hero: {
          stamp: true,
          background: `${this.$config.platform}-fp2024-bg-hero.webp`,
          backgroundMobile: `${this.$config.platform}-fp2024-bg-hero-mobile.webp`,
        },
        footer: {
          background: `${this.$config.platform}-fp2024-bg-footer.webp`,
          backgroundMobile: `${this.$config.platform}-fp2024-bg-footer-mobile.webp`,
        },
      },
      videoJsLoaded: false,
      bannerClosed: false,
      countdownEndDate: this.saleEndDate(),
      promotion: {},
      countdown: {
        days: '00',
        hours: '00',
        minutes: '00',
        seconds: '00',
      },
      instructorsTitle: 'Learn Data Science with <span class="highlight blue">Instructors</span> from<br><span class="smaller">Meta, Netflix, Google, Spotify, IKEA, etc.</span>',
      videos: [
        {
          name: 'Lee McFarling',
          image: 'lee',
          from: 'USA',
          id: 6315230915112,
          quote: 'After 6 months of learning with 365 Data Science, I was getting job interviews as a business analyst or a data analyst.',
          job: 'Data Analyst, Astreya',
        },
        {
          name: 'Meghan Verschoor',
          image: 'meghan',
          from: 'Canada',
          id: 6315228912112,
          quote: 'I find the platform really easy to follow through with and learn the skills that I needed to apply to my job. I highly recommend it.',
          job: 'Director, Data Science & Management, Turnstone Biologics',
        },
        {
          name: 'Nada Abdulwahab',
          image: 'nada',
          from: 'Egypt',
          id: 6315228795112,
          quote: '365 gave me the basics to start my career […] and still helps me enhance my skills and push my career to a higher level.',
          job: 'Senior Data Engineer, VOIS',
        },
        {
          name: 'Julián Elizondo',
          image: 'julián',
          from: 'Mexico',
          id: 6343656185112,
          quote: 'If it wasn’t for 365 Data Science and the confidence it gave me, I wouldn’t have landed my current job.',
          job: 'Tableau Developer, Schneider Electric',
        },
        {
          name: 'Mihaela Paparinova',
          image: 'mihaela',
          from: 'Bulgaria',
          id: 6341101204112,
          quote: 'The program helped me excel in my studies and deepen my understanding of niche data science concepts.',
          job: 'Data Science Student, Sofia University',
        },
        {
          name: 'Ernest Asena',
          image: 'ernest',
          from: 'Kenya',
          id: 6315231920112,
          quote: 'Eight months into the program, I started applying for entry-level positions, and I landed a job as a junior data scientist.”',
          job: 'Data Insights Analytics Cloud Engineer, AWS',
        },
        {
          name: 'Ahmed Eldessouky',
          image: 'ahmed',
          from: 'Egypt',
          id: 6327293863112,
          quote: 'I was impressed with the quality of the video lessons, and the topics and different tools the program covers.',
          job: 'Business Developer, Squadio',
        },
      ],
      heroAnimation: {
        strings: [
          'data scientist',
          'data analyst',
          'business analyst',
          'ML engineer',
          'AI engineer',
        ],
        index: 0,
        animate: false,
      },
      salaryAnimation: {
        activate: false,
        animatedOnScroll: false,
      },
      companies: [
        { icon: 'meta-new' },
        { icon: 'g' },
        { icon: 'spotify' },
        { icon: 'netflix' },
        { icon: 'ikea' },
        { icon: 'coca-cola' },
        { icon: 'bbc' },
        { icon: 'pwc-new' },
        { icon: 'morgan-stanley' },
      ],
      companiesSmall: [
        { icon: 'oracle' },
        { icon: 'amazon' },
        { icon: 'walmart' },
        { icon: 'alphabet' },
        { icon: 'meta-new' },
      ],
      promoCardOptions: {
        labelText: 'Exclusive Offer',
        discountedPriceCharge: '/year',
        billedText: 'Billed annually',
        countdown: false,
        buttonText: 'Save 57% Now',
        subscribeClass: 'subscribe-annual',
        promoPlanType: 'annual',
        version: '_v1',
      },
      instructors: [
        {
          name: 'Davis Balaba',
          slug: 'davis-balaba',
        },
        {
          name: 'Danielle Thé',
          slug: 'danielle-the',
        },
        {
          name: 'Bernard Marr',
          slug: 'bernard-marr',
        },
        {
          name: 'Anastasia Kuznetsova',
          slug: 'anastasia-kuznetsova',
        },
        {
          name: 'Jeff Lee',
          slug: 'jeff-li-1',
        },
        {
          name: 'Shashank Kalanithi',
          slug: 'shashank-kalanithi-1',
        },
        {
          name: 'Lauren Newbould',
          slug: 'lauren-newbould-1',
        },
        {
          name: 'Olivier Maugain',
          slug: 'olivier-maugain',
        },
        {
          name: 'Randy Rosseel',
          slug: 'randy-rosseel',
        },
        {
          name: 'Tina Huang',
          slug: 'tina-huang',
        },
        {
          name: 'Ken Jee',
          slug: 'ken-jee',
        },
      ],
      courseGroupIdx: 0,
    };
  },
  head() {
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
    ]);

    // const brightcoveSrc = [
    //   { src: 'https://players.brightcove.net/6258000438001/FHDad0Z7G_default/index.min.js' },
    //   { src: 'https://players.brightcove.net/videojs-quality-menu/1/videojs-quality-menu.min.js' },
    // ];

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
        { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
        { rel: 'preconnect', href: 'https://fonts.gstatic.com' },
        { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Source+Code+Pro:wght@700,600&display=swap' },
      ],
      meta: this.metaProps({ ...this.page, metaImage: 'og-campaign-ds-future-proof-2024.webp' }),

      script: [
        // ...brightcoveSrc,
        linkedData,
        {
          src:
            'https://players.brightcove.net/6258000438001/FHDad0Z7G_default/index.min.js',
          async: true,
        },
      ],
    };
  },
  computed: {
    cGroups() {
      const mapped = [
        'AI Engineer',
        'Machine Learning Engineer',
        'Data Analyst',
        'Data Scientist',
        'Business Intelligence Analyst',
        'Data Engineer',
        'Tableau Developer',
        'Power BI Developer',
        'Business Analyst',
      ];

      return mapped.map((t) => this.coursesGroups.find((g) => g.name === t) || {});
    },
    cGroup() {
      return this.cGroups[this.courseGroupIdx] || {};
    },
    cGroupCourses() {
      return this.cGroup.courses.map((id) => {
        if (typeof id === 'object') {
          return id;
        }

        return this.courses.find((c) => c.id === id) || {};
      }).filter((v) => Object.keys(v).length);
    },
    salary() {
      const [salary, year] = this.cGroup.description.earn.split('/');
      const salaryBolded = `<span class="letter bold">${salary.split('').join('</span><span class="letter bold">')}</span>`;
      const yearNormal = `<span class="letter">${year.split('').join('</span><span class="letter">')}</span>`;

      return `${salaryBolded}<span class="letter">/</span>${yearNormal}`;
    },
    limitedOfferOptions() {
      const price = this.page && this.page.prices.price ? this.page.prices.price : 119;
      const baseAnnualPrice = this.page && this.page.prices.basePriceComparison ? this.page.prices.basePriceComparison : 432;

      return {
        preTitleSlot: true,
        htmltitle: 'Data and AI Education to Thrive in an AI-Driven World ',
        promoPlanType: 'annual',
        comparisonPlanType: 'monthly',
        comparisonPlanBilledText: 'Billed Monthly',
        promoPlanBilledText: 'Billed Annually',
        regularPrice: this.$options.filters.formatCurrency(this.page.prices.basePrice, this.prices.currency, { minimumFractionDigits: 0 }),
        discountedPrice: this.$options.filters.formatCurrency(price, this.prices.currency, { minimumFractionDigits: 0 }),
        billedComparisonPrice: this.$options.filters.formatCurrency(baseAnnualPrice, this.prices.currency, { minimumFractionDigits: 0 }),
        discountedPriceCharge: '/year',
        billedComparisonCharge: '/month',
        promoPlanSubscribeClass: 'subscribe-annual',
        comparisonSubscribeClass: 'subscribe-monthly',
        promoPlanButtonText: `Get ${this.promotion.discount}% OFF`,
        buttonClass: 'submit',
        comparisonPlanButtonText: 'Sign up Now',
        vat: 'VAT may be applied',
        badges: this.$config.platform === 'ds',
      };
    },
    bottomBannerOptions() {
      return {
        counterWithDiscount: true,
        subscribeClass: 'submit campaign-1',
        buttonText: 'Buy Annual Plan',
        className: this.promotion.final ? 'final-promo' : '',
        futureProof: true,
      };
    },
    footerFeatures() {
      const data = [
        { name: `${this.stats.rounded.courses} data and AI courses` },
        { name: 'Employer-recognized certificates' },
        { name: 'World-class instructors' },
        { name: 'Resume builder' },
        { name: 'Engaging bite-sized lessons' },
        { name: 'Resume feedback' },
        { name: `${this.stats.rounded.exercises}+ practical exercises` },
        { name: 'Portfolio advice' },
        { name: 'Structured career tracks' },
        { name: 'Social channels for peer discussions' },
        { name: 'Custom learning path' },
        { name: 'A gamified learning platform' },
        { name: 'Real-world data projects' },
        { name: '30-day refund' },
        { name: 'Hands-on exams' },
        { name: 'Price-lock guarantee' },
      ];

      if (this.$store.state.layout === 'mobile') {
        return [
          ...data.filter((v, i) => i % 2 === 0),
          ...data.filter((v, i) => i % 2 === 1),
        ];
      }

      return data;
    },
    prices() {
      return this.page.prices;
    },
    priceSplitted() {
      return (+this.prices.price || 0).toString().split('.');
    },
  },
  created() {
    this.countdownEndDate = this.saleEndDate();
  },
  async mounted() {
    /* eslint-disable no-param-reassign */
    /* eslint-disable no-underscore-dangle */
    /* eslint-disable func-names */
    function hotJar(h, o, t, j, a, r) {
      // eslint-disable-next-line prefer-rest-params
      h.hj = h.hj || function () { (h.hj.q = h.hj.q || []).push(arguments); };
      h._hjSettings = { hjid: 1214727, hjsv: 6 };
      // eslint-disable-next-line prefer-destructuring
      a = o.getElementsByTagName('head')[0];
      r = o.createElement('script'); r.async = 1;
      r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
      a.appendChild(r);
    }

    if (this.$config.platform === 'ds') {
      hotJar(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
    }

    await this.$pollValue(() => typeof videojs !== 'undefined');
    this.videoJsLoaded = true;
    // Hero animation
    setInterval(() => {
      this.heroAnimation.animate = true;
      setTimeout(() => {
        this.heroAnimation.index = (this.heroAnimation.index + 1) % this.heroAnimation.strings.length;
      }, 300);

      setTimeout(() => {
        this.heroAnimation.animate = false;
      }, 600);
    }, 1200);

    this.$nextTick(() => this.toggleBanner());
    await this.$pollValue(() => typeof videojs !== 'undefined');
    this.videoJsLoaded = true;
    this.handleScroll();

    let stop = false;
    const interval = setInterval(() => {
      if (stop) {
        clearInterval(interval);
      }

      const nowDate = new Date();
      if (dateInRightInterval(nowDate)) {
        this.countdownEndDate = this.saleEndDate();
      }

      if (nowDate > new Date(this.promotion.endDateFinal)) {
        stop = true;
        window.location.href = `${this.$config.siteUrl}/offer-expired/`;
      }
    }, 1000);

    window.addEventListener('scroll', this.handleScroll);
    this.$once('hook:beforeDestroy', () => window.removeEventListener('scroll', this.handleScroll));
    this.handleScroll();
  },
  methods: {
    handleScroll() {
      if (process.client) {
        const salary = this.$el.querySelector('.animation-salary');
        if (salary && !this.salaryAnimation.animatedOnScroll) {
          const salaryBounds = salary.getBoundingClientRect();
          if (salaryBounds.top < window.innerHeight * 0.7) {
            this.switchCourseGroup(0, true);
            this.salaryAnimation.animatedOnScroll = true;
          }
        }

        const scroll = () => {
          if (this.$store.state.layout === 'mobile' || this.bannerClosed) {
            return;
          }
          this.$nextTick(() => this.toggleBanner());
        };

        window.addEventListener('scroll', scroll);
        this.$once('hook:beforeDestroy', () => window.removeEventListener('scroll', scroll));
      }
    },
    instructorImage(instructor) {
      return require(`../assets/images/landing/lp-sl-m-${instructor.slug}${this.$store.state.layout === 'mobile' ? '-mobile' : ''}.webp`);
    },
    switchCourseGroup(idx, force = false) {
      if (idx === this.courseGroupIdx && !force) {
        return;
      }

      if (this.salaryAnimation.activate) {
        this.salaryAnimation.activate = false;
      }

      let startAnimationAfter = 10;

      if (!force && this.$store.state.layout === 'mobile') {
        const coursesSlider = this.$el.querySelector('.courses-extended .infinite-carousel');
        if (coursesSlider) {
          startAnimationAfter = 500;
          coursesSlider.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }

      this.salaryAnimation.animatedOnScroll = true;

      setTimeout(() => {
        this.courseGroupIdx = idx;
        this.$nextTick(() => {
          let width = 0;
          const holder = this.$el.querySelector('.animation-salary .holder');
          const letters = holder.querySelectorAll('.letter');
          for (let idx2 = 0; idx2 < letters.length; idx2 += 1) {
            const letter = letters[idx2];
            width += letter.offsetWidth;
          }
          this.salaryAnimation.width = width;
          this.salaryAnimation.activate = true;
          setTimeout(() => {
            for (let idx3 = 0; idx3 < letters.length; idx3 += 1) {
              const letter = letters[idx3];
              setTimeout(() => {
                letter.classList.add('show');
              }, idx3 * 100);
            }
          }, 200);
        });
      }, startAnimationAfter);
    },
    subscribe() {
      const plan = this.$store.getters['subscriptions/annual'] || {};
      this.$store.dispatch('dataLayer/addEvent', { event: 'addToCart', value: { ...plan, planTitle: plan.title, currency: plan.currency.value } });

      return this.$router.push({
        path: '/checkout/',
        query: { plan: 'annual', coupon: this.promotion.coupon },
      });
    },
    toggleBanner() {
      const bottomBanner = document.querySelector('.bottom-banner');
      const hero = document.querySelector('.hero .companies');
      const footer = document.querySelector('.limited-offer-holder');
      let hideFromTop = false;
      let hideFromBottom = false;

      if (bottomBanner) {
        if (hero) {
          hideFromTop = hero.getBoundingClientRect().top - 100 > 0;
        }

        if (footer) {
          hideFromBottom = footer.getBoundingClientRect().top - 400 < 0;
        }

        if (hideFromTop || hideFromBottom) {
          bottomBanner.classList.remove('fixed');
        } else {
          bottomBanner.classList.add('fixed');
        }
      }
    },
    closeBanner() {
      this.bannerClosed = true;
    },
    saleEndDate() {
      const campaignData = CAMPAIGN_PROMOTIONS['future-proof-2024'];
      const promoOptions = getPromotion(campaignData.promos, this.promotion);
      this.promotion = {
        buttonText: 'Start Now',
        ...promoOptions.nextPromotion,
        ...campaignData.templates,
      };
      if (promoOptions.refresh) {
        setTimeout(() => {
          window.$nuxt.refresh();
        }, 40000);
      }

      return new Date(this.promotion.endDate);
    },
  },
};
</script>

<style lang="scss" scoped>
@keyframes heroAnimation {
  0% {
    transform: rotateX(0deg);
  }

  50% {
    transform: rotateX(90deg);
  }

  100% {
    transform: rotateX(0deg);
  }
}

@include bp($bp-desktop) {
  .mobile-only,
  .laptop-only {
    display: none !important;
  }
}

.block {
  display: block;
}

.highlight,
::v-deep .highlight {
  font-weight: 700;

  &.campaign-1,
  &.blue {
    color: var(--c-fp2024-1);

    &.special {
      text-shadow: 0 2.872px 6.976px rgba(37, 244, 238, 0.5);
    }
  }

  &.campaign-2,
  &.yellow {
    color: var(--c-fp2024-2);
  }

  &.white {
    font-weight: 600;
    color: var(--main-light);
  }
}

.btn-primary,
::v-deep .btn-primary {
  color: var(--main-dark);
  border: 2px solid var(--c-fp2024-2);
  background-color: var(--c-fp2024-2);
  font-weight: 600;
  font-size: 21px;
  border-radius: 5px;
  margin: 0 auto;
  display: block;
  height: 58px;
  line-height: 18px;
  max-width: 350px;
  width: 100%;
  margin-top: var(--spacing-60);
  padding-left: 5px;
  padding-right: 5px;

  &.submit {
    transition: all 50ms linear;
    border-color: var(--main-dark);
    background-color: var(--c-fp2024-2);
    margin-top: 20px;
    margin-bottom: 12px;
    line-height: 30px;
  }

  &:not(:disabled) {
    &:hover,
    &:active,
    &:focus {
      color: var(--main-dark);
      background-color: var(--c-fp2024-2);

      &:not(.submit) {
        filter: drop-shadow(0 0 30px var(--c-fp2024-2));
        border-color: var(--c-fp2024-2);
      }

      &.submit {
        background-color: var(--c-fp2024-2);
        border-color: var(--main-dark);
        outline: 2px solid var(--main-dark) !important;
      }
    }
  }
}

::v-deep .limited-offer .btn-primary.submit,
::v-deep .bottom-banner .btn-primary.submit {
  background-color: var(--c-mw-2);

  &:not(:disabled) {
    &:hover,
    &:active,
    &:focus {
      color: var(--main-dark);
      background-color: var(--c-mw-2);
    }
  }
}

.landing-page-v2 {
  background-color: var(--main-dark);
  color: var(--main-light);
  min-height: 100vh;

  :not(.hero) .btn-primary {
    width: 350px;
  }

  h1 {
    font-size: 48px;
    font-weight: 500;
    line-height: 1.1;
    margin-bottom: 40px;

    .code-block img {
      height: 35px;
      display: block;
      margin-bottom: var(--spacing-8);
    }
  }

  .muted {
    color: var(--main-light-shade-4);
  }

  .subtitle {
    font-size: 18px;
    font-weight: 500;
  }

  .animation {
    font-family: "Source Code Pro", monospace;
    font-optical-sizing: auto;
    font-weight: 600;
    font-style: normal;
    display: inline-block;
    font-size: 18px;
    line-height: 18px;
    width: 180px;
    height: 23px;
    color: var(--main-dark);
    background-color: #F0F0F2;
    border: 1px solid #ABAFB5;
    border-radius: 4px;
    text-align: center;
    transform-style: preserve-3d;

    &.animate {
      animation: heroAnimation 0.6s ease-in-out;
    }
  }

  .static-salary-container {
    width: 188px;
    display: inline-block;
  }

  .animation-salary {
    vertical-align: middle;
    display: inline-block;
    width: 188px;

    .holder {
      display: block;
      white-space: nowrap;
      overflow: hidden;
      width: 0;

      &.animation {
        text-align: left;
        line-height: 23px;
      }

      &.active {
        transition: width 0.5s;
        width: var(--width, 188px);
      }
    }

    ::v-deep {
      .letter {
        font-weight: normal;
        opacity: 0;
        transition: all 0.1s linear;

        &.show {
          opacity: 1;
        }

        &:first-child {
          padding-left: 4px;
        }

        &:last-child {
          padding-right: 4px;
        }

        &.bold {
          font-weight: 700;
        }
      }
    }
  }

  .section {
    background-size: cover;
    background-position: bottom;
    padding-top: 100px;
    padding-bottom: 100px;

    &.second {
      ::v-deep .infinite-head .h4 {
        text-align: center;
        font-size: 40px;

        .smaller {
          font-size: 32px;
        }
      }
    }
  }

  .hero {
    min-height: 740px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-image: url('../assets/images/campaigns/ds-fp2024-bg-hero.webp');
    background-size: auto 100%;
    background-position: center;
    background-repeat: no-repeat;

    ::v-deep .trusted-companies {
      margin-top: var(--spacing-36);
      padding-top: var(--spacing-36);
      border-color: rgba(255, 255, 255, 0.24);

      .logos {
        flex-wrap: wrap;
        justify-content: center;
        row-gap: 32px;
        column-gap: 66px;

        .logo {
          svg {
            height: 24px;
            color: var(--main-light-shade-3);

            g[fill] {
              fill: var(--main-light-shade-3);
            }

            &.pwc {
              height: 28px;
            }
          }
        }
      }
    }

    h1 {
      font-size: 44px;
      font-weight: 900;
      margin-bottom: 24px;

      .small {
        font-size: 32px;
        font-weight: 500;
      }

      .code-block {
        width: 292px;
      }
    }

    .holder {
      padding: 0 0 0 var(--spacing-30);

      .card {
        margin-left: auto;
        padding: var(--spacing-36);
        background-color: var(--main-light);
        border-radius: 20px;
        box-shadow: 0 16px 30px 0 rgba(0, 12, 31, 0.25);
        position: relative;
        max-width: 422px;
        color: var(--main-dark);

        &:before {
          content: '';
          position: absolute;
          top: 4px;
          right: -4px;
          bottom: -8px;
          left: 10px;
          background: var(--c-mw-1);
          z-index: -1;
        }

        .label {
          color: var(--main-dark-light-4);
          font-size: 20px;
          font-weight: 600;
          margin: 0;
        }

        .old-price {
          font-size: 30px;
          line-height: 110%;
          font-weight: 500;
          margin-top: var(--spacing-12);
          margin-bottom: 0;
          position: relative;
          display: inline-block;

          .del:after {
            content: '';
            width: 106px;
            height: 2px;
            background: var(--main-dark-light-1);
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }
        }

        .new-price {
          font-size: 72px;
          font-weight: 500;
          line-height: 1.1;
          margin-top: var(--spacing-4);
          margin-bottom: var(--spacing-4);
          display: flex;
          align-items: flex-end;
          justify-content: flex-start;

          .medium {
            font-size: 30px;
            line-height: 50px;
          }

          .small {
            font-size: 21px;
            font-weight: 400;
            line-height: 50px;
          }
        }

        .text {
          margin: 12px 0;
          font-size: 21px;
          line-height: 1.4;
        }

        .card-text {
          font-size: 14px;
          line-height: 150%;
          color: var(--main-dark-light-2);
          text-align: center;
          margin: 0;
        }

        .stamp {
          position: absolute;
          top: 36px;
          right: 36px;

          img {
            display: block;
            width: 100px;
          }
        }
      }

      .footer-text {
        display: none;
      }
    }
  }

  .instructors-slider {
    ::v-deep {
      .infinite-carousel .carousel .inner > * {
        width: 210px;
        margin-right: 30px;

        img {
          display: block;
          border-radius: 20px;
        }
      }
    }
  }

  .instructors-footer {
    ::v-deep .trusted-companies {
      margin-top: 30px;
      padding-top: 32px;
      border-color: rgba(255, 255, 255, 0.24);

      .logos {
        .logo {
          svg {
            height: 30px;

            &,
            g[fill] {
              color: var(--main-light-shade-3);
            }

            path[fill],
            g[fill] {
              fill: var(--main-light-shade-3);
            }
          }
        }
      }
    }

    .trustpilot-review {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 40px;
      margin-bottom: 30px;

      svg {
        height: 40px;
        margin-right: 26px;
      }

      ::v-deep .course-rating {
        font-size: 28px;
        font-weight: 500;
        color: var(--main-light-shade-4);

        .rating {
          margin-right: 20px;
        }

        .rating-stars {
          height: auto;

          svg {
            width: 38px;
            height: 38px;
          }
        }
      }
    }
  }

  .btn-holder {
    display: flex;
    justify-content: center;
    margin-top: 32px;
  }

  .stats {
    background-color: var(--main-dark-light-1);

    ::v-deep .stats-icons {
      margin-top: 0;

      > div {
        align-items: center;
      }

      svg {
        width: 90px;
        height: 90px;
      }
    }
  }

  .courses-extended {
    .head {
      text-align: center;

      h2 {
        font-size: 40px;
      }

      h3 {
        margin: 40px 0;
      }

      ul {
        list-style: none;
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 16px;
        padding-left: 0;

        li {
          padding: 20px;
          font-size: 18px;
          line-height: 1.1;
          color: var(--main-light);
          background-color: var(--main-dark-light-1);
          border: 2px solid transparent;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover,
          &.active {
            border-color: var(--accent);
            background-color: var(--main-dark-light-2);
          }
        }
      }

      .description {
        font-size: 24px;
        font-weight: 500;
        margin-top: 60px;
        margin-bottom: 0;
      }
    }
  }

  ::v-deep .infinite-carousel {
    .controls {
      margin-top: 0;
    }

    .carousel {
      padding-top: 10px;
      margin-top: -10px;
      padding-bottom: 10px;
      margin-bottom: -10px;

      .card {
        margin: 0 var(--spacing-30) 0 0;
        width: 370px;
        vertical-align: top;

        .course-card-container {
          min-height: 422px;
        }

        &:not(.course-card) {
          flex-direction: column;
          align-items: flex-start;
          height: auto;
          border-radius: 0;

          .image-wrapper img {
            display: block;
            max-width: 100%;
            max-height: 100%;
            border-radius: 7px;
          }

          .card-title {
            margin-top: var(--spacing-20);
            font-size: 32px;
            color: var(--main-light);
          }

          .card-desc {
            color: var(--main-dark-shade-4);
            font-size: 21px;
            line-height: 140%;
          }
        }

        &.course-card {
          .course-card-image {
            height: 220px;
          }

          .course-card-body {
            padding: var(--spacing-20) var(--spacing-24) 20px;

            .course-name {
              margin-bottom: var(--spacing-8);
            }

            .course-rating {
              color: var(--main-light);
            }

            .info {
              color: var(--main-light);
              margin: 0;

              .dot {
                background: var(--main-light);
              }
            }
          }
        }
      }
    }
  }

  .master {
    background: linear-gradient(135deg, rgba(86, 191, 199, 0.2) 0.94%, rgba(86, 191, 199, 0) 100%), #000C1F;

    .muted {
      font-size: 21px;
    }

    img {
      display: block;
      max-width: 100%;
    }
  }

  .graduations {
    background-size: cover;
    background-position: bottom;

    ::v-deep {
      .title-highlight {
        font-size: 32px;

        p {
          font-size: 28px;
        }

        svg {
          height: 38px;
        }
      }
    }

    .heading img {
      height: 44px;
      display: inline-block;
      vertical-align: bottom;
    }
  }

  .dream {
    background: linear-gradient(135deg, rgba(86, 191, 199, 0.2) 0.94%, rgba(86, 191, 199, 0) 100%), #000C1F;
    text-align: center;

    .title-description {
      font-size: 32px;
      font-weight: 500;
      margin: 0;
    }

    .row {
      margin-top: 60px;
      margin-bottom: 18px;
    }

    .col {
      svg {
        color: var(--accent);
      }

      .title {
        font-size: 28px;
        font-weight: 500;
        color: var(--accent);
      }

      .description {
        font-size: 18px;
        font-weight: 400;
        line-height: 1.5;
      }
    }
  }

  ::v-deep .limited-offer-holder {
    position: relative;

    .limited-offer {
      position: relative;
      z-index: 2;
      color: var(--main-light);

      h2 {
        margin-bottom: var(--spacing-40);
      }

      .subtitle {
        font-size: 18px;
        line-height: 1.1;
        text-transform: uppercase;
        color: var(--c-fp2024-2);
        margin-bottom: 12px;
      }

      .heading {
        font-size: 40px;

        img {
          display: block;
          margin: 0 auto;
        }

        .text-2 {
          font-size: 44px;
          font-weight: 800;
        }

        .text-3 {
          font-size: 40px;
        }
      }

      .price {
        margin-top: var(--spacing-20);
        margin-bottom: var(--spacing-30);

        .regular-price {
          align-self: flex-start;
          margin-top: 10px;
          font-weight: 500;

          &:before {
            background-color: var(--main-light);
          }
        }

        .discounted-price {
          line-height: 70px;
          font-weight: 900;
          font-size: 60px;

          .promo-price {
            color: var(--c-cm-2);
          }

          .body-1 {
            font-weight: 400;
          }
        }
      }

      .plan-features {
        margin-bottom: var(--spacing-50);

        .feature {
          position: relative;
          line-height: 150%;
          margin-bottom: var(--spacing-20);

          svg {
            [fill] {
              fill: var(--c-mw-1);
            }

            [stroke] {
              stroke: var(--c-mw-1);
            }
          }
        }
      }

      hr {
        display: none;
      }

      .switch-holder {
        span:not(.unactive) {
          color: var(--main-light);
        }

        .switch {
          width: 45.69px;
          height: 22px;

          .slider:before {
            width: 16px;
            height: 16px;
            top: 3px;
            left: 3px;
          }

          // input:not(:checked) + .slider {
          //   background-color: var(--c-tyf-1);
          // }

          input:checked + .slider {
            background-color: var(--c-tyf-1);

            &:before {
              transform: translateX(23px);
            }
          }
        }
      }

      .btn-primary {
        margin-top: 0;
      }

      .badges {
        margin-top: 40px;
      }

      .vat-note {
        margin-top: var(--spacing-24);
        color: var(--main-dark-shade-4);
        font-weight: 600;
        font-size: 18px;
      }
    }

    .limited-offer-background {
      position: absolute;
      top: 0;
      left: 50%;
      bottom: 0;
      width: 100%;
      height: calc(100% + 50px);
      object-fit: cover;
      transform: translateX(-50%);
    }
  }

  ::v-deep .bottom-banner {
    background-image: url('../assets/images/campaigns/future-proof/fpc-bg-bottom-banner.webp');
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    height: 110px;
    padding: 0;
    border-top: 5px solid var(--extreme-yellow);

    &.final-promo {
      background-image: url('../assets/images/campaigns/future-proof/fpc-bg-footer.webp');
    }

    .container {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }

    .heading {
      font-size: 28px;
      line-height: 1.1;
      font-weight: 700;

      p {
        margin: 0;
      }

      .code-block {
        height: 26px;
        margin-bottom: var(--spacing-8);

        img {
          display: block;
          max-width: 100%;
          max-height: 100%;
        }
      }

      .main {
        font-size: 28px;
        text-transform: uppercase;
        color: var(--extreme-blue);
        text-shadow: 2px 2px 4px rgba(25, 245, 238, 0.5);
      }

      .text-2 {
        font-size: 24px;

        .highlight {
          font-size: 28px;
        }
      }
    }

    .btn-primary {
      width: 220px;
      height: 58px;
      margin-left: 0;
      margin-right: 0;
      background: var(--extreme-yellow);
      color: var(--main-dark);
      line-height: 20px;
      border: 0;

      &:hover {
        background-color: var(--extreme-yellow);
      }

      &:active {
        background-color: var(--extreme-yellow);
      }
    }

    .icon-close {
      top: 19px;
      right: 14px;

      path[fill] {
        fill: var(--main-dark-light-4);
      }
    }
  }

  @include bp($bp-laptop) {
    .section.second {
      ::v-deep .infinite-head .h4 {
        font-size: 32px;

        .smaller {
          font-size: 28px;
        }
      }
    }

    .courses-extended .head {
      ul li {
        font-size: 14px;
      }

      .description {
        font-size: 21px;
      }
    }

    .dream {
      .title-description {
        font-size: 28px;
      }

      .col {
        .title {
          font-size: 24px;
        }
      }
    }

    .landing-footer {
      .title-description {
        font-size: 28px;
      }

      .title-description-2 {
        font-size: 32px;
      }
    }

    .limited-offer-holder {
      .limited-offer {
        .limited-offer-container {
          width: 700px;
          max-width: 700px;
        }

        .subtitle {
          font-size: 21px;
        }

        .heading {
          &,
          .text-2,
          .text-3 {
            font-size: 32px;
          }

          img {
            height: 60px;
          }
        }

        .subtitle .title-inline {
          width: 240px;
        }

        .price {
          margin-bottom: 46px;

          .discounted-price {
            font-size: 54px;
          }
        }

        .btn-primary {
          max-width: 310px;
        }
      }

      .limited-offer-background {
        bottom: -160px;
      }
    }

    ::v-deep .bottom-banner {
      .heading {
        .main {
          font-size: 24px;
        }

        .text-2 {
          font-size: 21px;

          .highlight {
            font-size: 32px;
          }
        }
      }

      .btn-primary {
        width: 180px;
        font-size: 18px;
      }
    }
  }

  @include bp($bp-mobile) {
    .desktop-only {
      display: none !important;
    }

    .btn-primary.submit,
    ::v-deep .btn-primary.submit {
      line-height: 18px;
    }

    .section {
      padding-top: 60px;
      padding-bottom: 60px;
      background-size: contain;
      background-position: bottom;
      background-repeat: no-repeat;
    }

    :not(.hero) .btn-primary {
      width: 100%;
      max-width: 327px;
    }

    h1 {
      font-size: 44px;
    }

    .subtitle {
      font-size: 20px;
    }

    .hero {
      background-image: url('../assets/images/campaigns/ds-fp2024-bg-hero-mobile.webp');
      padding-top: 108px;

      h1 {
        text-align: center;
        font-size: 40px;

        .code-block {
          width: auto;
        }

        img {
          display: block;
          margin-left: auto;
          margin-right: auto;
        }
      }

      ::v-deep .trusted-companies {
        margin-top: 40px;
        padding-top: 40px;
        margin-bottom: 60px;

        .logos {
          column-gap: 40px;

          .logo svg {
            height: 20px;
          }
        }
      }

      .holder {
        padding: 0;

        .card {
          padding: 24px;

          .stamp {
            top: 24px;
            right: 24px;
          }

          .card-text {
            font-size: 12px;
          }
        }
      }

      .mobile-only {
        margin-top: 40px;

        .subtitle {
          font-weight: 400;
        }
      }
    }

    .section.second {
      margin-top: 60px;

      ::v-deep {
        .infinite-head .h4 {
          font-size: 32px;

          .smaller {
            font-size: 28px;
          }
        }

        .infinite-carousel .carousel .inner > * {
          width: 327px;
        }
      }
    }

    .instructors-footer {
      ::v-deep {
        .trusted-companies {
          margin-bottom: 30px;

          .title {
            margin-top: 0;
            margin-bottom: 30px;
          }
        }

        .trustpilot-review {
          margin-top: 0;
          align-items: baseline;

          > svg {
            height: 26px;
            flex: 0 0 auto;
          }

          .course-rating {
            font-size: 20px;

            .rating-stars svg {
              width: 25px;
              height: 25px;
            }
          }
        }
      }
    }

    .courses-extended {
      .head {
        .highlight {
          display: block;
        }

        ul {
          gap: 12px;
          justify-content: flex-start;
        }

        .description {
          margin-top: 32px;

          .highlight.blue {
            display: block;
          }
        }
      }

      .courses {
        padding-top: 32px;
        padding-bottom: 0;
      }

      .btn-holder {
        margin-top: 0;
      }

      ::v-deep .infinite-carousel .carousel .card {
        width: 327px;
      }
    }

    .master {
      text-align: center;
    }

    .graduations {
      ::v-deep {
        .col {
          padding-left: 26px;
          padding-right: 26px;
        }
      }
    }

    .dream {
      h2 {
        font-size: 32px;
      }

      .title-description {
        font-size: 28px;
      }
    }

    ::v-deep .limited-offer-holder {
      padding-top: 60px;
      padding-bottom: 100px;

      .limited-offer {
        .container {
          h2 {
            margin: 0 auto var(--spacing-40);
          }
        }

        .plan-features {
          text-align: left;
          display: block;
          width: 100%;

          .feature {
            margin-bottom: 6px;
          }
        }

        .price {
          align-items: flex-start;
        }

        .subtitle {
          font-size: 18px;
          line-height: 110%;
          display: block;
          text-align: center;

          .title-inline {
            display: block;
            width: 258px;
            margin: 10px 0;
          }
        }

        .heading {
          font-size: 32px;

          .text-2 {
            font-size: 32px;
            margin: 12px 0;
          }

          .text-3 {
            font-size: 32px;
          }
        }
      }

      .limited-offer-background {
        top: 0;
        bottom: 0;
        width: 100%;
        object-fit: cover;
        object-position: top;
        height: 100%;
      }
    }
  }
}
</style>
